import { useEffect, useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import { convertWeiToEth } from '@pkg/utils';

import { type ActivityListEntryType } from '@app/client';
import {
    callGetTokenDetailsList,
    callGetTokensListingsActivity,
    callGetTokensSalesActivity,
} from '@app/client/resources/marketplace';
import { type YacsTokensListProps } from '@app/client/resources/marketplace/types';

export const useGetTokensActivity = (props: YacsTokensListProps) => {
    const [tokensActivity, setTokensActivity] = useState<ActivityListEntryType[]>([]);

    const queryHandlers = [
        { handlerFunction: callGetTokensSalesActivity, handlerId: 'callGetTokensSalesActivity' },
        //{ handlerFunction: callGetTokensTransactionsActivity, handlerId: 'callGetTokensTransactionsActivity' },
        { handlerFunction: callGetTokensListingsActivity, handlerId: 'callGetTokensListingsActivity' },
    ];
    const responses = useQueries({
        queries: queryHandlers.map((handler) => ({
            queryKey: [
                handler.handlerId,
                ...props.tokens.map((token) => token.token_ids.map((id) => `${token.contract}:${id}`).join(',')),
            ],
            queryFn: ({ signal }: { signal?: AbortSignal }) =>
                handler.handlerFunction({ ...props, settings: { signal } }),
        })),
    });

    const isLoading = responses.some((response) => response.isLoading);

    useEffect(() => {
        if (isLoading) return;
        const tokenActivity = responses.flatMap((query) => query.data);
        const tokensDataProps =
            tokenActivity.reduce<{ contract: string; token_ids: string[] }[]>((acc, listingData) => {
                if (!acc.some((contractData) => contractData.contract === listingData?.contract_address)) {
                    acc.push({
                        contract: listingData?.contract_address || '',
                        token_ids: [listingData?.id || ''],
                    });
                    return acc;
                }
                return acc.map((contractData) => {
                    if (contractData.contract === listingData?.contract_address) {
                        return { ...contractData, token_ids: [...contractData.token_ids, listingData.id] };
                    }
                    return contractData;
                });
            }, []) || [];

        callGetTokenDetailsList({
            tokens: tokensDataProps,
        }).then((tokensData) => {
            tokensData?.ok &&
                tokensData?.value &&
                setTokensActivity(
                    tokenActivity
                        .reduce<ActivityListEntryType[]>((acc, eventData) => {
                            const tokenContents = tokensData?.value.contents?.find(
                                (token) =>
                                    token.token_id === eventData?.id &&
                                    token.collection === eventData?.contract_address,
                            );
                            if (eventData && tokenContents && tokenContents.status !== 'Missing') {
                                acc.push({
                                    ...eventData,
                                    uuid: uuid(),
                                    image: tokenContents?.image_url || '',
                                    name: tokenContents?.name || eventData.id,
                                    value: convertWeiToEth(eventData?.value || 0, 2),
                                    owner_yat: '',
                                    twitter_handle: '',
                                    wallet_address: eventData.addressTo || eventData.addressFrom,
                                } as ActivityListEntryType);
                            }
                            return acc;
                        }, [])
                        .sort((a, b) => {
                            if (a.date > b.date) {
                                return -1;
                            }
                            if (a.date < b.date) {
                                return 1;
                            }
                            return 0;
                        }),
                );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    return { tokensActivity, isLoading };
};
